/**
 * OptionalScale Component
 *
 * Compact 1-10 scale input that can be skipped (null).
 * When unset, shows a "Set" button. Once set, shows a slider with a clear button.
 */

import { X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface OptionalScaleProps {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
}

function getColor(value: number): string {
  if (value <= 2) return "text-red-500";
  if (value <= 4) return "text-orange-500";
  if (value <= 6) return "text-yellow-600 dark:text-yellow-400";
  if (value <= 8) return "text-emerald-500";
  return "text-green-500";
}

export function OptionalScale({ label, value, onChange }: OptionalScaleProps) {
  const isSet = value !== null;

  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-foreground">{label}</label>
        {isSet ? (
          <div className="flex items-center gap-2">
            <span
              className={cn("text-sm font-bold tabular-nums", getColor(value))}
            >
              {value}/10
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(null)}
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              title="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(5)}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            Set
          </Button>
        )}
      </div>
      {isSet && (
        <Slider
          value={[value]}
          onValueChange={(values) => onChange(values[0])}
          min={1}
          max={10}
          step={1}
          className="mt-1"
        />
      )}
    </div>
  );
}
